import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Lock } from 'lucide-react';
import { moduleRefusal, type PlanModule, type ReportRange } from '@tailoring-crm/shared';
import { UpgradePrompt } from '../billing/upgrade-prompt.js';
import { EmptyState } from '../feedback/empty-state.js';
import { PageHeader } from '../layout/page-header.js';
import { Button, ListSkeleton } from '@tailoring-crm/ui';
import { API_BASE_URL } from '../../lib/api/base-url.js';
import { useHasModule, useSubscriptionSnapshot } from '../../lib/billing/entitlements.js';
import { RangePicker, type RangePickerValue } from './range-picker.js';

/**
 * The frame all five reports share: a title, the period picker, the export button, and the
 * period the server actually resolved.
 *
 * That last line matters more than it looks. The picker sends "last month" and the server
 * decides what that means against its own clock; printing the resolved days back — "1 to 31
 * July" — is what lets an owner tell the difference between a quiet month and a wrong window.
 *
 * The export link is a plain `<a>` to the API rather than a fetch-and-blob. The server answers
 * with a `Content-Disposition`, so the browser's own download machinery handles it — including
 * on the Android WebView where a script-driven save silently does nothing.
 *
 * A caller the report is not for is told so, once, instead of being shown a screen of failed
 * requests. The API would refuse them anyway; this is the same refusal in a sentence. A report
 * that is not in the shop's plan is the same idea with a different sentence — what it is, which
 * plan has it and what that costs — built by the shared function the API refuses with.
 */
export function ReportShell({
  title,
  description,
  range,
  onRangeChange,
  resolved,
  csvPath,
  isPending,
  allowed = true,
  module = 'core',
  children,
  controls,
}: {
  title: string;
  description?: string;
  range: RangePickerValue;
  onRangeChange: (next: RangePickerValue) => void;
  resolved: ReportRange | undefined;
  /**
   * Omitted by a report that has no CSV twin, which hides the export button rather than offering
   * a file that does not exist. The pipeline report is the one: four of its six sections ignore
   * the date range, and a file named for a window it did not apply is a file somebody misreads
   * next quarter.
   */
  csvPath?: string;
  isPending: boolean;
  allowed?: boolean;
  /** The plan module the report belongs to. The page should skip its request when it is missing. */
  module?: PlanModule;
  children: ReactNode;
  /** Extra per-report controls — the basis toggle, the "not seen in" selector. */
  controls?: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const inPlan = useHasModule(module);
  const snapshot = useSubscriptionSnapshot();

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <EmptyState
          icon={Lock}
          title={t('reports.noAccessTitle')}
          description={t('reports.noAccessHint')}
        />
      </>
    );
  }

  if (!inPlan && snapshot) {
    return (
      <>
        <PageHeader title={title} {...(description ? { description } : {})} />
        <UpgradePrompt refusal={moduleRefusal(snapshot, module)} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        {...(csvPath
          ? {
              action: (
                <Button asChild variant="outline">
                  <a href={`${API_BASE_URL}${csvPath}`}>
                    <Download aria-hidden="true" />
                    {t('reports.export')}
                  </a>
                </Button>
              ),
            }
          : {})}
      />

      <div className="flex flex-col gap-5">
        <RangePicker value={range} onChange={onRangeChange} />
        {controls}

        {resolved ? (
          <p className="text-xs text-muted-foreground" role="status">
            {resolved.fromDay === resolved.toDay
              ? resolved.fromDay
              : t('reports.resolvedRange', { from: resolved.fromDay, to: resolved.toDay })}
          </p>
        ) : null}

        {isPending ? <ListSkeleton rows={4} /> : children}
      </div>
    </>
  );
}

/** One headline figure. Used by every report, so they all read the same way. */
export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): ReactElement {
  return (
    <div className="rounded-lg border border-border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-xl font-semibold tabular-nums">{value}</dd>
      {hint ? <dd className="mt-0.5 text-xs text-muted-foreground">{hint}</dd> : null}
    </div>
  );
}
